// SAB-431: gates analytics/marketing pixel initialization behind consent.
//
// Replaces the previous top-level `initGA()` (App.tsx) and `initAnalytics()`
// (main.tsx) calls that fired before any consent decision.
//
// Behavior:
//   - On mount and on every consent change, if the user has accepted the
//     analytics category, init GA4 (idempotent — has internal `initialized`
//     guard in lib/gtag.ts) and init Mixpanel (one-shot per session via
//     local flag) + fire app_opened.
//   - When consent is missing or revoked, NO pixel scripts are injected.
//     The script tags never enter the page.
//
// NOTE on Mixpanel `record_sessions_percent: 100` (src/lib/analytics.ts):
//   That config records 100% of sessions where Mixpanel is initialized.
//   With this consent gate in place, the effective coverage becomes
//   "100% of analytics-CONSENTED sessions." Behavior change is intentional
//   and called out in the SAB-431 PR. If we ever want to lower the rate to
//   reduce data volume, change `record_sessions_percent` in lib/analytics.ts
//   — not here.
//
// SAB-434 follow-up will add the marketing-pixel branch (Meta Pixel,
// Google Tag, Reddit Pixel) gated by hasConsent('marketing').
import { useEffect } from 'react'
import { hasConsent, subscribeConsent } from '@/lib/consent'
import { initGA } from '@/lib/gtag'
import { initAnalytics, trackEvent } from '@/lib/analytics'

export default function AnalyticsGate() {
  useEffect(() => {
    let mixpanelInited = false

    const sync = () => {
      if (hasConsent('analytics')) {
        initGA()
        if (!mixpanelInited) {
          initAnalytics()
          trackEvent('app_opened')
          mixpanelInited = true
        }
      }
      // No-op when consent is missing. GA's internal `initialized` guard
      // (lib/gtag.ts:5) means once a previous session injected the script,
      // a no-consent reload simply doesn't re-inject — GA_ID is gone from
      // the page. Mixpanel's mpInitialized flag is per-mount.
    }

    sync()
    return subscribeConsent(sync)
  }, [])

  return null
}
